import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '@env/environment';
import { ManualExtractionRepository } from '@service/associate-extraction/manual/manual-extraction.repository';
import {
  SampleForExtraction,
  ManualExtractionData
} from '@model/associate-extraction/manual/manual-extraction.model';

/**
 * Backend response DTOs
 */
interface SpecimenForExtractionResponse {
  specimenCode: string;
  lgCode: string | null;
  patientFirstName: string;
  patientLastName: string;
  identificationType: string;
  identificationNumber: string;
  birthDate: string;
  gender: string;
}

interface ManualExtractionRequest {
  specimenCode: string;
  lgCode: string | null;
  concentration: number;
  ratio260_280: number;
  ratio260_230: number;
  extractionMethodId: string;
  wellLocation: string;
}

interface ManualExtractionResponse {
  extractionId: string;
  specimenId: string;
  lgCode: string | null;
  concentration: number;
  ratio260_280: number;
  ratio260_230: number;
  extractionMethodId: string;
  wellLocation: string;
  status: string;
  manualEntry: boolean;
  responsibleName: string;
  extractionDate: string;
  createdAt: string;
  message: string;
}

/**
 * HTTP Repository Implementation for Manual Extraction operations
 * Connects to backend endpoints:
 * - GET /specimens/extraction/search/{specimenCode}
 * - POST /specimens/extraction/manual
 *
 * HU23-920839: Carga Manual de Datos de Extracción
 */
@Injectable({
  providedIn: 'root'
})
export class HttpManualExtractionRepository extends ManualExtractionRepository {
  private readonly http = inject(HttpClient);
  private readonly BASE_URL = `${environment.laboratory.baseUrl}${environment.laboratory.context}/specimens/extraction`;

  override searchSample(specimenCode: string): Observable<SampleForExtraction> {
    return this.http
      .get<SpecimenForExtractionResponse>(
        `${this.BASE_URL}/search/${specimenCode}`
      )
      .pipe(
        map((response) => this.mapToSampleForExtraction(response)),
        catchError((error) => {
          console.error('Error searching sample:', error);
          return throwError(() => error);
        })
      );
  }

  override registerExtraction(data: ManualExtractionData): Observable<void> {
    const request: ManualExtractionRequest = {
      specimenCode: data.specimenCode,
      lgCode: data.lgCode || null,
      concentration: data.concentration,
      ratio260_280: data.ratio260_280,
      ratio260_230: data.ratio260_230,
      extractionMethodId: data.extractionMethodId,
      wellLocation: data.wellLocation
    };

    return this.http
      .post<ManualExtractionResponse>(`${this.BASE_URL}/manual`, request)
      .pipe(
        map(() => void 0),
        catchError((error) => {
          console.error('Error registering extraction:', error);
          return throwError(() => error);
        })
      );
  }

  private mapToSampleForExtraction(
    response: SpecimenForExtractionResponse
  ): SampleForExtraction {
    return {
      specimenCode: response.specimenCode,
      lgCode: response.lgCode,
      patientFirstName: response.patientFirstName,
      patientLastName: response.patientLastName,
      identificationType: response.identificationType,
      identificationNumber: response.identificationNumber,
      birthDate: new Date(response.birthDate),
      gender: response.gender
    };
  }
}
